import React, { useState, useEffect, useReducer } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { toast } from "react-toastify";
import SearchIcon from "@material-ui/icons/Search";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach(contact => {
      const contactIndex = state.findIndex(c => c.id === contact.id);
      if (contactIndex !== -1) state[contactIndex] = contact;
      else newContacts.push(contact);
    });

    return [...state, ...newContacts];
  }

  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  dialogContent: { overflowY: "scroll", ...theme.scrollbarStyles, },

  contactCard: { borderRadius: "20px", padding: "10px", marginBottom: "5px", },

  contactCardActionArea: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    borderRadius: "20px",
    padding: "5px",
  },

  contactName: { fontSize: "16px", },

  contactNumber: { fontSize: "13px", color: "#959EAC", },

  maxWidth: { width: "100%" },
}));

const DispatchBlackListContactsModal = ({ open, onClose }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();

  const [contacts, dispatch] = useReducer(reducer, []);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(null);

  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", { params: { searchParam, pageNumber, typeOfContact: "0" } });
          const filteredContacts = data.contacts.filter(contact => !!contact.number || !!contact.lid);

          dispatch({ type: "LOAD_CONTACTS", payload: filteredContacts });
          setHasMore(data.hasMore);
          setLoading(false);
        }
        catch (exception) {
          console.log("Dispatch Black List Contacts Modal - useEffect 1 Exception:", exception);
          toastError(exception);
        }
      };

      fetchContacts();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [open, searchParam, pageNumber]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchWhatsapps = async () => {
        try {
          const { data } = await api.get("/AllWhatsapps");
          setWhatsapps(data);

          const defaultWhatsapp = data.filter(whatsapp => whatsapp.isDefault)[0];
          setSelectedWhatsapp(defaultWhatsapp);

          setLoading(false);
        }
        catch (exception) {
          console.log("Dispatch Black List Contacts Modal - useEffect 2 Exception:", exception);
          toastError(exception);
        }
      };

      fetchWhatsapps();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [open]);



  //  ***************
  //  ** Functions **
  //  ***************
  const resetStates = () => {
    setSearchParam("");
    setPageNumber(1);
    setHasMore(false);

    setSelectedContact(null);
    setSelectedWhatsapp(whatsapps.filter(whatsapp => whatsapp.isDefault)[0]);

    setLoading(false);
    dispatch({ type: "RESET" });
  };

  const handleClose = () => {
    onClose();
    resetStates();
  };

  const handleOpenConfirmationModal = (contact) => {
    setSelectedContact(contact);
    setConfirmationModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setSelectedContact(null);
    setConfirmationModalOpen(false);
  };

  const handleWhatsappSelect = (event) => {
    setSelectedWhatsapp(event.target.value);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;    
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(previousPageNumber => previousPageNumber + 1);
  };

  const handleAddContactToBlackList = async () => {
    try {
      if (loading) return;

      if (!selectedContact) {
        toast.info(i18n.t("dispatchBlackListContactsModal.toasts.notSelectedContact"));
        return;
      }

      if (!selectedWhatsapp) {
        toast.info(i18n.t("dispatchBlackListContactsModal.toasts.notSelectedWhatsapp"));
        return;
      }
      
      const response = await api.post("/dispatchBlackListContacts", { contactId: selectedContact.id, whatsappId: selectedWhatsapp.id });
      if (response.data.message) toast.info(i18n.t(response.data.message));
      else toast.success(i18n.t("dispatchBlackListContactsModal.toasts.successAdded"));
    }
    catch (exception) {
      console.log("Dispatch Black List Contacts Modal - handleAddContactToBlackList Exception:", exception);
      toast.error(i18n.t(exception));
    }
    finally {
      setConfirmationModalOpen(false);
      setSelectedContact(null);
      setSelectedWhatsapp(whatsapps.filter(whatsapp => whatsapp.isDefault)[0]);
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={`${i18n.t("dispatchBlackListContactsModal.confirmationModal.title")} ${selectedContact?.name}?`}
        open={confirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={async () => await handleAddContactToBlackList()}
      >
        <FormControl variant="outlined" className={classes.maxWidth}>
          <InputLabel>{i18n.t("dispatchBlackListContactsModal.confirmationModal.connection")}</InputLabel>

          <Select
            value={selectedWhatsapp}
            onChange={event => handleWhatsappSelect(event)}
            label={i18n.t("dispatchBlackListContactsModal.confirmationModal.connection")}
          >
            {whatsapps.map(whatsapp => (
              <MenuItem key={whatsapp.id} value={whatsapp}>{whatsapp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </ConfirmationModal>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
        <DialogTitle>
          <span>{i18n.t("dispatchBlackListContactsModal.title.add")}</span>

          <br /><br />

          <TextField
            autoFocus
            fullWidth
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "gray" }}/></InputAdornment> }}
          />
        </DialogTitle>

        <DialogContent className={classes.dialogContent} onScroll={handleScroll} dividers>
          {contacts.map(contact => (
            <Card
              id={`card-${contact.id}`}
              className={classes.contactCard}
              onClick={() => handleOpenConfirmationModal(contact)}
            >
              <CardActionArea className={classes.contactCardActionArea}>
                <Avatar alt={contact?.name} src={contact?.profilePicUrl} />

                <CardContent>
                  <Typography className={classes.contactName} gutterBottom component="h3">{contact.name}</Typography>
                  <Typography className={classes.contactNumber} gutterBottom component="h3">{contact.number || contact.lid}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </DialogContent>

        <DialogActions>
          <Button color="primary" variant="contained" onClick={handleClose}>
            {i18n.t("dispatchBlackListContactsModal.buttons.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DispatchBlackListContactsModal;
